// SPDX-FileCopyrightText: 2023-2025 Open Pioneer project (https://github.com/open-pioneer)
// SPDX-License-Identifier: Apache-2.0
import { createLogger, Error } from "@open-pioneer/core";
import { sourceId } from "open-pioneer:source-info";
import { ServiceOptions } from "../Service";
import { ApplicationLifecycleListener } from "../api";
import { ErrorId } from "../errors";

const LOG = createLogger(sourceId);

interface References {
    listeners: ApplicationLifecycleListener[];
}

export class ApplicationLifecycleEventService {
    #listeners: [serviceId: string, listener: ApplicationLifecycleListener][];

    constructor(options: ServiceOptions<References>) {
        const listeners = options.references.listeners;
        const listenersMeta = options.referencesMeta.listeners;
        this.#listeners = listeners.map((listener, index) => [
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            listenersMeta[index]!.serviceId,
            listener
        ]);
    }

    /** @internal */
    emitLifecycleEvent(event: "after-start" | "before-stop"): void {
        const methodName = (() => {
            switch (event) {
                case "after-start":
                    return "afterApplicationStart";
                case "before-stop":
                    return "beforeApplicationStop";
                default:
                    throw new Error(ErrorId.INTERNAL, `Invalid event '${event}'`);
            }
        })();
        for (const [serviceId, listener] of this.#listeners) {
            try {
                listener[methodName]?.();
            } catch (e) {
                LOG.error(`Unexpected error from application lifecycle listener ${serviceId}`, e);
            }
        }
    }
}
